import React, { useEffect, useState } from "react";
import {
  Button,
  Grid,
  Pagination,
  Paper,
  Stack,
  Typography,
  Box,
  Card,
  Divider,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Tooltip,
  alpha,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import TableItem from "../../../components/TableItem";
import { getImportData, DiactivateItemById } from "../../../services/Import";
import CircularProgress from "@mui/material/CircularProgress";
import { useHistory } from "react-router";
import InputField from "../../../FormComponents/InputField";
import Alart from "../../../components/Alart";
import { getSaleProductData } from "../../../services/Sales";
import SaleProductForm from "./SaleProductForm";
import AddIcon from "@mui/icons-material/Add";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchIcon from "@mui/icons-material/Search";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ReceiptIcon from "@mui/icons-material/Receipt";
import { RoleBasedRender } from "../../../components/RoleBaseRender";

// Custom styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: "0 4px 20px 0 rgba(0,0,0,0.05)",
}));

const HeaderCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2.5),
  borderRadius: theme.shape.borderRadius * 1.5,
  // background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.light} 90%)`,
  color: theme.palette.primary.main,
  marginBottom: theme.spacing(3),
  boxShadow: "0 8px 16px 0 rgba(0,0,0,0.1)",
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 1.5,
  textTransform: "none",
  fontWeight: 600,
  boxShadow: "0 4px 12px 0 rgba(0,0,0,0.1)",
}));

const FilterContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius * 1.5,
  backgroundColor: alpha(theme.palette.primary.light, 0.05),
  marginBottom: theme.spacing(3),
}));

const PaginationContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  padding: theme.spacing(2),
  marginTop: theme.spacing(2),
}));

let _saleProductListState = null;

function SaleProducts() {
  const history = useHistory();

  // State management
  const [openDisableWarning, setOpenDisableWarning] = useState(false);
  const [msgDisableWarning, setMsgDisableWarning] = useState({
    title: "Warning",
    content: "Are you sure you want to disable this product?",
  });
  const [disableItemId, setDisableItemId] = useState(null);
  const [rows, setRows] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openForm, setOpenForm] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // Table columns configuration
  const columns = [
    { id: "date", label: "Date", minWidth: 100 },
    { id: "barcode", label: "Barcode", minWidth: 120 },
    { id: "product", label: "Product", minWidth: 180 },
    { id: "billNumber", label: "Bill Number", minWidth: 120 },
    {
      id: "sllingQty",
      label: "Quantity",
      minWidth: 100,
      format: (value) => value.toLocaleString("en-US"),
    },
    {
      id: "paidStatus",
      label: "Status",
      minWidth: 120,
      format: (value) => (
        <Chip
          label={value}
          color={
            value === "Paid"
              ? "success"
              : value === "Partial"
              ? "warning"
              : "error"
          }
          size="small"
          variant="outlined"
        />
      ),
    },
    { id: "action", label: "Actions", minWidth: 120 },
  ];

  // Create data row factory function
  function createData(
    date,
    barcode,
    product,
    billNumber,
    sllingQty,
    paidStatus,
    action,
    sellingId,
    productId
  ) {
    return {
      date,
      barcode,
      product,
      billNumber,
      sllingQty,
      paidStatus,
      action,
      sellingId,
      productId,
    };
  }

  // Filter state
  const [filter, setFilter] = useState({
    _barcode: "",
    _productName: "",
    _billNumber: "",
    _addedBy: "",
    _customer: "",
    _paidStatus: "",
  });

  // Initial data load
  useEffect(() => {
    if (_saleProductListState) {
      const { page: savedPage, filter: savedFilter } = _saleProductListState;
      _saleProductListState = null;
      setPage(savedPage);
      setFilter(savedFilter);
      fetchData(savedFilter, savedPage);
    } else {
      fetchData(filter, page);
    }
  }, []);

  // Pagination handler
  const handleChange = async (event, value) => {
    setPage(value - 1);
    await fetchData(filter, value - 1);
  };

  // Fetch data from API
  const fetchData = async (productFilter, pageNo) => {
    setLoading(true);
    try {
      let result = await getSaleProductData(pageNo, rowsPerPage, productFilter);
      let saleProductList = result.content;

      // Set total rows and pages
      setTotalPages(result.totalPages);
      setTotalRows(result.totalElements);

      const newSet = [];
      if (saleProductList) {
        for (let x = 0; x < saleProductList.length; x++) {
          // Format date to be more readable
          const formattedDate = new Date(
            saleProductList[x].saleDate
          ).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
          });

          // Set data in new set list to display in the table
          newSet.push(
            createData(
              formattedDate,
              saleProductList[x].product.barcode,
              saleProductList[x].product.name,
              saleProductList[x].billNumber,
              saleProductList[x].sellingQty,
              saleProductList[x].paidStatus,
              saleProductList[x].saleId,
              saleProductList[x].product.product_id
            )
          );
        }
      }

      // Set rows to table
      setRows(newSet);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Action handler for table actions
  const handleAction = async (event, id) => {
    switch (event) {
      case "view":
        _saleProductListState = { page, filter };
        history.push("/template/sale_product_view/" + id);
        break;
      case "edit":
        history.push("/template/sale_product_edit/" + id);
        break;
      case "disable":
        disableProduct(id);
        break;
      default:
        return;
    }
  };

  // Disable product confirmation
  const disableProduct = async (id) => {
    setDisableItemId(id);
    setOpenDisableWarning(true);
  };

  // Disable warning action buttons
  function DisableWarningActionButtons() {
    return (
      <>
        <Button
          variant="contained"
          color="error"
          onClick={async () => {
            await DiactivateItemById(disableItemId);
            setOpenDisableWarning(false);
            await fetchData(filter, page);
          }}
        >
          Disable
        </Button>
        <Button
          variant="outlined"
          onClick={() => {
            setOpenDisableWarning(false);
          }}
        >
          Cancel
        </Button>
      </>
    );
  }

  // Reset filters
  const resetFilters = () => {
    setFilter({
      _barcode: "",
      _productName: "",
      _billNumber: "",
      _addedBy: "",
      _customer: "",
      _paidStatus: "",
    });
  };

  // Handle filter change
  const handleFilterChange = (name, val) => {
    setFilter({
      ...filter,
      [name]: val,
    });
  };

  // Toggle filter visibility
  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  return (
    <>
      <Alart
        open={openDisableWarning}
        message={msgDisableWarning}
        actions={<DisableWarningActionButtons />}
      />
      <StyledPaper elevation={0}>
        {openForm ? (
          <SaleProductForm setOpenForm={setOpenForm} />
        ) : (
          <>
            <HeaderCard>
              <Grid container alignItems="center" spacing={2}>
                <Grid item>
                  <ReceiptIcon fontSize="large" />
                </Grid>
                <Grid item xs>
                  <Typography variant="h5" fontWeight="bold">
                    Sales Management
                  </Typography>
                  <Typography variant="body2">
                    View and manage all sales transactions
                  </Typography>
                </Grid>
                <Grid item>
                  <RoleBasedRender
                    allowedRoles={["SALE_PRODUCT_CREATE", "ROLE_ADMIN"]}
                  >
                    <ActionButton
                      variant="contained"
                      color="primary"
                      startIcon={<AddIcon />}
                      onClick={() => setOpenForm(true)}
                    >
                      New Sale
                    </ActionButton>
                  </RoleBasedRender>
                </Grid>
              </Grid>
            </HeaderCard>

            <Box
              mb={3}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <TextField
                placeholder="Search by product name or bill number..."
                variant="outlined"
                size="small"
                sx={{ width: "40%" }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                onChange={(e) => {
                  handleFilterChange("_productName", e.target.value);
                  if (
                    e.target.value.length > 2 ||
                    e.target.value.length === 0
                  ) {
                    fetchData(
                      { ...filter, _productName: e.target.value },
                      page
                    );
                  }
                }}
              />
              <Stack direction="row" spacing={2}>
                <Tooltip title="Toggle Filters">
                  <ActionButton
                    variant="outlined"
                    color="primary"
                    startIcon={<FilterListIcon />}
                    onClick={toggleFilters}
                  >
                    Filters
                  </ActionButton>
                </Tooltip>
                <Chip
                  label={`${totalRows} Records`}
                  variant="outlined"
                  color="primary"
                />
              </Stack>
            </Box>

            {showFilters && (
              <FilterContainer>
                <Typography variant="subtitle2" fontWeight="bold" mb={2}>
                  Advanced Filters
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={2}>
                    <InputField
                      name="_barcode"
                      value={filter._barcode}
                      onChange={(event, newInputValue) =>
                        handleFilterChange(event, newInputValue)
                      }
                      type="text"
                      label="Barcode"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <InputField
                      name="_billNumber"
                      value={filter._billNumber}
                      onChange={(event, newInputValue) =>
                        handleFilterChange(event, newInputValue)
                      }
                      type="text"
                      label="Bill Number"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <InputField
                      name="_addedBy"
                      value={filter._addedBy}
                      onChange={(event, newInputValue) =>
                        handleFilterChange(event, newInputValue)
                      }
                      type="text"
                      label="Added By"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <InputField
                      name="_customer"
                      value={filter._customer}
                      onChange={(event, newInputValue) =>
                        handleFilterChange(event, newInputValue)
                      }
                      type="text"
                      label="Customer"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <InputField
                      name="_paidStatus"
                      value={filter._paidStatus}
                      onChange={(event, newInputValue) =>
                        handleFilterChange(event, newInputValue)
                      }
                      type="text"
                      label="Paid Status"
                      fullWidth
                    />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={2}
                    display="flex"
                    alignItems="center"
                  >
                    <Stack direction="row" spacing={1}>
                      <Tooltip title="Apply Filters">
                        <ActionButton
                          variant="contained"
                          color="primary"
                          size="small"
                          onClick={() => fetchData(filter, page)}
                        >
                          Apply
                        </ActionButton>
                      </Tooltip>
                      <Tooltip title="Reset Filters">
                        <IconButton
                          color="default"
                          onClick={() => {
                            resetFilters();
                            fetchData({}, page);
                          }}
                        >
                          <RestartAltIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Grid>
                </Grid>
              </FilterContainer>
            )}

            <Divider sx={{ my: 2 }} />

            {isLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
                <CircularProgress />
              </Box>
            ) : rows.length === 0 ? (
              <Box sx={{ textAlign: "center", p: 5 }}>
                <Typography variant="body1" color="textSecondary">
                  No sales records found. Try adjusting your filters or add a
                  new sale.
                </Typography>
              </Box>
            ) : (
              <TableItem
                columns={columns}
                rows={rows}
                page={page}
                setPage={setPage}
                tablePagin={false}
                totalPages={totalPages}
                handleAction={handleAction}
                showActions={{
                  view: "Sale product view",
                  edit: "Sale product edit",
                  disable: "Sale product Diactvate",
                }}
                actionRoles={{
                  view: ["SALE_PRODUCT_VIEW", "ROLE_ADMIN"],
                  edit: ["SALE_PRODUCT_UPDATE", "ROLE_ADMIN"],
                  disable: ["SALE_PRODUCT_DELETE", "ROLE_ADMIN"],
                }}
                tableProps={{
                  sx: {
                    "& .MuiTableRow-root:hover": {
                      backgroundColor: alpha("#000", 0.04),
                    },
                  },
                }}
              />
            )}

            {totalPages > 1 && (
              <PaginationContainer>
                <Pagination
                  count={totalPages}
                  page={page + 1}
                  variant="outlined"
                  shape="rounded"
                  color="primary"
                  onChange={handleChange}
                  size="medium"
                />
              </PaginationContainer>
            )}
          </>
        )}
      </StyledPaper>
    </>
  );
}

export default SaleProducts;
